import type { InterimApplication } from '../types/database';

const mappings = {
  breakdown: {
    previouslyAgreed: 'previouslyAgreed', thisApplication: 'thisApplication', cumulativeToDate: 'cumulativeToDate',
  },
  retention: {
    retentionPercentage: 'percentage', currentRelease: 'currentRelease', cumulativeRetained: 'cumulativeRetained',
    cumulativeReleased: 'cumulativeReleased', retentionBalance: 'balance',
  },
  summary: {
    grossValuation: 'grossValuation', lessRetention: 'lessRetention', netValuation: 'netValuation',
    lessPreviouslyCertified: 'lessPreviouslyCertified', amountDue: 'amountDue', vat: 'vat', totalIncVat: 'totalIncVat',
  },
} as const;

const outcomeMappings = {
  paymentOutcomeStatus: 'status', paymentNoticeReference: 'noticeReference', paymentNoticeDate: 'noticeDate',
  sumStatedDue: 'sumStatedDue', amountReceived: 'amountReceived', paymentReceivedDate: 'paymentDate', paymentOutcomeNotes: 'notes',
} as const;

/** Read typed columns through the existing view model without changing a saved
 * application. Older responses fall back to JSON only when the column is absent.
 * Null removes the legacy value, so a cleared receipt cannot reappear from cache.
 */
export function readInterimFinancialColumns(application: InterimApplication): InterimApplication {
  const record = application as unknown as Record<string, unknown>;
  const result = { ...application };
  for (const [container, fields] of Object.entries(mappings)) {
    const source = application[container as keyof typeof mappings];
    const projected: Record<string, unknown> = { ...source };
    for (const [column, field] of Object.entries(fields)) {
      if (!Object.prototype.hasOwnProperty.call(application, column)) continue;
      if (record[column] == null) delete projected[field];
      else projected[field] = record[column];
    }
    // Legacy view models represent missing nullable columns by absent keys.
    // Preserve unrelated metadata (retention ledger and carry-forward markers).
    Object.assign(result, { [container]: projected });
  }
  const outcome: Record<string, unknown> = { ...application.summary?.paymentOutcome };
  for (const [column, field] of Object.entries(outcomeMappings)) {
    if (!Object.prototype.hasOwnProperty.call(application, column)) continue;
    if (record[column] == null) delete outcome[field];
    else outcome[field] = record[column];
  }
  result.summary.paymentOutcome = outcome.status
    ? outcome as unknown as NonNullable<InterimApplication['summary']['paymentOutcome']>
    : undefined;
  return result;
}
